import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userPanelApi } from 'api';
import { usePanel } from 'contexts/panelContext';
import { CreateGoalPayload, UpdateGoalPayload } from 'types/payloads/GoalPayload';

interface UseGoalsOptions {
	onMutationSuccess?: () => void;
}

interface ToggleGoalStepPayload {
	goalId: number;
	stepId: number;
}

const useGoals = (pairId: number, options?: UseGoalsOptions) => {
	const { currentProgramMembership } = usePanel();
	const queryClient = useQueryClient();
	const { onMutationSuccess } = options ?? {};

	const {
		refetch: getGoals,
		data: fetchedGoals,
		isLoading: goalLoading,
		isError: goalError,
	} = useQuery({
		queryKey: ['goals', pairId],
		staleTime: 30000,
		queryFn: () => userPanelApi.getGoals(+pairId, currentProgramMembership.id),
		enabled: !!currentProgramMembership?.id && !!pairId,
	});

	const {
		mutate: updateGoal,
		mutateAsync: updateGoalAsync,
		isLoading: isUpdatingGoal,
	} = useMutation({
		mutationFn: (goalPayload: UpdateGoalPayload) =>
			userPanelApi.updateGoal(+pairId, currentProgramMembership.id, goalPayload),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['goals', pairId] }),
				queryClient.invalidateQueries({ queryKey: ['videocall-goals'] }),
				queryClient.invalidateQueries({ queryKey: ['todos'] }),
			]);
			onMutationSuccess?.();
		},
	});

	const {
		mutate: deleteGoal,
		mutateAsync: deleteGoalAsync,
		isLoading: isDeletingGoal,
	} = useMutation({
		mutationFn: (goalId: number) => userPanelApi.deleteGoal(+pairId, currentProgramMembership.id, goalId),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['goals', pairId] }),
				queryClient.invalidateQueries({ queryKey: ['videocall-goals'] }),
				queryClient.invalidateQueries({ queryKey: ['todos'] }),
			]);
			onMutationSuccess?.();
		},
	});

	const {
		mutate: saveNewGoal,
		mutateAsync: saveNewGoalAsync,
		isLoading: isSavingGoal,
	} = useMutation({
		mutationFn: (goalPayload: CreateGoalPayload) =>
			userPanelApi.postGoal(+pairId, currentProgramMembership.id, goalPayload),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['goals', pairId] }),
				queryClient.invalidateQueries({ queryKey: ['videocall-goals'] }),
				queryClient.invalidateQueries({ queryKey: ['todos'] }),
			]);
			onMutationSuccess?.();
		},
	});

	const {
		mutate: signGoals,
		mutateAsync: signGoalsAsync,
		isLoading: isSigningGoals,
	} = useMutation({
		mutationFn: () => userPanelApi.signGoal(+pairId, currentProgramMembership.id),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['goals', pairId] }),
				queryClient.invalidateQueries({ queryKey: ['videocall-goals'] }),
				queryClient.invalidateQueries({ queryKey: ['contract'] }),
				queryClient.invalidateQueries({ queryKey: ['videocall-contract'] }),
				queryClient.invalidateQueries({ queryKey: ['todos'] }),
				queryClient.invalidateQueries({ queryKey: ['sessions', { pairId: Number(pairId) }] }),
			]);
		},
	});

	const {
		mutate: toggleGoalStep,
		mutateAsync: toggleGoalStepAsync,
		isLoading: isTogglingGoalStep,
	} = useMutation({
		mutationFn: ({ goalId, stepId }: ToggleGoalStepPayload) =>
			userPanelApi.toggleGoalStep(+pairId, currentProgramMembership.id, goalId, stepId),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['goals', pairId] }),
				queryClient.invalidateQueries({ queryKey: ['videocall-goals'] }),
				queryClient.invalidateQueries({ queryKey: ['todos'] }),
			]);
		},
	});

	return {
		getGoals,
		updateGoal,
		updateGoalAsync,
		deleteGoal,
		deleteGoalAsync,
		saveNewGoal,
		saveNewGoalAsync,
		signGoals,
		signGoalsAsync,
		toggleGoalStep,
		toggleGoalStepAsync,
		fetchedGoals,
		goalLoading,
		goalError,
		isUpdatingGoal,
		isDeletingGoal,
		isSavingGoal,
		isSigningGoals,
		isTogglingGoalStep,
	};
};

export default useGoals;
